import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

import snowVertexShader from "./shaders/snow.vert";
import snowFragmentShader from "./shaders/snow.frag";
import imprintFragmentShader from "./shaders/imprint.frag";

const TEXTURE_SIZE = 512;
const SNOW_SIZE = 10;

const MOVE_KEYS = new Set([
  "w",
  "a",
  "s",
  "d",
  "arrowup",
  "arrowdown",
  "arrowleft",
  "arrowright",
]);

const fullscreenVertexShader = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

function createRenderTarget() {
  return new THREE.WebGLRenderTarget(TEXTURE_SIZE, TEXTURE_SIZE, {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat,
    type: THREE.UnsignedByteType,
    depthBuffer: false,
    stencilBuffer: false,
  });
}

const SnowScene = forwardRef(function SnowScene(
  { ballSpeed, ballRadius, snowDepth, onDraggingChange },
  ref,
) {
  const { gl } = useThree();

  const ballRef = useRef();
  const keysRef = useRef(new Set());
  const draggingRef = useRef(false);

  const dragPoint = useMemo(() => new THREE.Vector3(), []);

  const dragPlane = useMemo(
    () => new THREE.Plane(new THREE.Vector3(0, 1, 0), -ballRadius),
    [ballRadius],
  );

  const targetA = useMemo(() => createRenderTarget(), []);
  const targetB = useMemo(() => createRenderTarget(), []);

  const targetsRef = useRef({
    read: targetA,
    write: targetB,
  });

  const simulation = useMemo(() => {
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const material = new THREE.ShaderMaterial({
      vertexShader: fullscreenVertexShader,
      fragmentShader: imprintFragmentShader,
      uniforms: {
        uPrevious: { value: null },
        uBallPosition: { value: new THREE.Vector2(0.5, 0.5) },
        uBallRadius: { value: 0.05 },
        uStrength: { value: 1 },
      },
      depthTest: false,
      depthWrite: false,
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const quad = new THREE.Mesh(geometry, material);

    scene.add(quad);

    return {
      scene,
      camera,
      material,
      geometry,
    };
  }, []);

  const snowMaterial = useMemo(
    () =>
      new THREE.ShaderMaterial({
        vertexShader: snowVertexShader,
        fragmentShader: snowFragmentShader,
        uniforms: {
          uHeightMap: { value: targetA.texture },
          uDepth: { value: snowDepth },
          uSnowColor: { value: new THREE.Color("#eef3f5") },
          uLightDirection: {
            value: new THREE.Vector3(-0.5, 1, 0.4).normalize(),
          },
        },
      }),
    [targetA],
  );

  function clearTargets() {
    const previousTarget = gl.getRenderTarget();
    const previousColor = new THREE.Color();
    const previousAlpha = gl.getClearAlpha();

    gl.getClearColor(previousColor);
    gl.setClearColor(0x000000, 1);

    gl.setRenderTarget(targetA);
    gl.clear(true, false, false);

    gl.setRenderTarget(targetB);
    gl.clear(true, false, false);

    gl.setRenderTarget(previousTarget);
    gl.setClearColor(previousColor, previousAlpha);

    targetsRef.current = {
      read: targetA,
      write: targetB,
    };

    snowMaterial.uniforms.uHeightMap.value = targetA.texture;
  }

  function stopDragging() {
    draggingRef.current = false;
    onDraggingChange?.(false);
  }

  function resetSimulation() {
    stopDragging();
    clearTargets();

    ballRef.current?.position.set(0, ballRadius, 0);
  }

  useImperativeHandle(
    ref,
    () => ({
      reset: resetSimulation,
    }),
    [ballRadius],
  );

  useEffect(() => {
    function handleKeyDown(event) {
      const target = event.target;

      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      if (key === "r") {
        event.preventDefault();
        resetSimulation();
        return;
      }

      if (MOVE_KEYS.has(key)) {
        event.preventDefault();
        keysRef.current.add(key);
      }
    }

    function handleKeyUp(event) {
      keysRef.current.delete(event.key.toLowerCase());
    }

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [ballRadius]);

  useEffect(() => {
    clearTargets();
  }, []);

  useEffect(
    () => () => {
      targetA.dispose();
      targetB.dispose();
      simulation.geometry.dispose();
      simulation.material.dispose();
      snowMaterial.dispose();
    },
    [targetA, targetB, simulation, snowMaterial],
  );

  useFrame((_, delta) => {
    const ball = ballRef.current;

    if (!ball) {
      return;
    }

    const movement = ballSpeed * delta;
    const keys = keysRef.current;

    if (keys.has("w") || keys.has("arrowup")) {
      ball.position.z -= movement;
    }

    if (keys.has("s") || keys.has("arrowdown")) {
      ball.position.z += movement;
    }

    if (keys.has("a") || keys.has("arrowleft")) {
      ball.position.x -= movement;
    }

    if (keys.has("d") || keys.has("arrowright")) {
      ball.position.x += movement;
    }

    const limit = SNOW_SIZE * 0.5 - ballRadius;

    ball.position.x = THREE.MathUtils.clamp(ball.position.x, -limit, limit);

    ball.position.z = THREE.MathUtils.clamp(ball.position.z, -limit, limit);

    ball.position.y = ballRadius - snowDepth * 0.25;

    simulation.material.uniforms.uBallPosition.value.set(
      ball.position.x / SNOW_SIZE + 0.5,
      0.5 - ball.position.z / SNOW_SIZE,
    );

    simulation.material.uniforms.uBallRadius.value = ballRadius / SNOW_SIZE;

    const { read, write } = targetsRef.current;

    simulation.material.uniforms.uPrevious.value = read.texture;

    const previousTarget = gl.getRenderTarget();

    gl.setRenderTarget(write);
    gl.render(simulation.scene, simulation.camera);
    gl.setRenderTarget(previousTarget);

    snowMaterial.uniforms.uHeightMap.value = write.texture;
    snowMaterial.uniforms.uDepth.value = snowDepth;

    targetsRef.current = {
      read: write,
      write: read,
    };
  });

  function handleBallPointerDown(event) {
    event.stopPropagation();

    draggingRef.current = true;
    onDraggingChange?.(true);

    event.target.setPointerCapture(event.pointerId);
  }

  function handleBallPointerMove(event) {
    if (!draggingRef.current) {
      return;
    }

    event.stopPropagation();

    if (!event.ray.intersectPlane(dragPlane, dragPoint)) {
      return;
    }

    const limit = SNOW_SIZE * 0.5 - ballRadius;

    ballRef.current.position.x = THREE.MathUtils.clamp(
      dragPoint.x,
      -limit,
      limit,
    );

    ballRef.current.position.z = THREE.MathUtils.clamp(
      dragPoint.z,
      -limit,
      limit,
    );
  }

  function handleBallPointerUp(event) {
    event.stopPropagation();
    stopDragging();

    if (event.target.hasPointerCapture(event.pointerId)) {
      event.target.releasePointerCapture(event.pointerId);
    }
  }

  return (
    <>
      <ambientLight intensity={0.8} />
      <directionalLight position={[-4, 8, 5]} intensity={1.5} />

      <mesh
        ref={ballRef}
        position={[0, ballRadius, 0]}
        onPointerDown={handleBallPointerDown}
        onPointerMove={handleBallPointerMove}
        onPointerUp={handleBallPointerUp}
        onPointerCancel={stopDragging}
      >
        <icosahedronGeometry args={[ballRadius, 5]} />
        <meshStandardMaterial color="#c96f45" roughness={0.72} metalness={0} />
      </mesh>

      <mesh
        position={[0, -snowDepth - 0.08, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <planeGeometry args={[18, 18]} />
        <meshStandardMaterial color="#252a2c" roughness={1} metalness={0} />
      </mesh>

      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[SNOW_SIZE, SNOW_SIZE, 256, 256]} />
        <primitive object={snowMaterial} attach="material" />
      </mesh>
    </>
  );
});

export default SnowScene;
